import _ from 'lodash';
import Injector from 'vue-inject';
import url from 'url';
import qs from 'qs';
import Debug from '@/util/debug';
import moment from 'moment-timezone';
import debounce from 'debounce';
import Vue from 'vue';

const debug = Debug('service:AdApi');

const POST = 'POST';
const GET = 'GET';
const HEAD = 'HEAD';
const PATCH = 'PATCH';
// const DELETE = 'DELETE';

const ADAPI_VERSION = 'v1';

const DAYS_BEFORE_TOKEN_REFRESH = 15; // days to expirity before we refresh token

export default class AdApiService {
  constructor(vue, apiUrlService, apiKeyService) {
    this.vue = vue;
    this.apiUrlService = apiUrlService;
    this.apiKeyService = apiKeyService;

    this.debouncedRefresh = debounce(() => {
      this.rawRequest(POST, 'auth/refresh')
        .then(token => this.apiKeyService.setKey(token))
        .then(() => debug.info('Refreshed token'))
        .catch(err => debug.error('Error while refreshing token: %o', err));
    }, 5000, true);
    this.eventBus = new Vue();
    this.ongoingRequests = {};
  }

  buildRequest(method, path, query, body) {
    let target = url.resolve(this.apiUrlService.getUrl(ADAPI_VERSION), path.replace(/^\//, ''));
    target = url.parse(target, true, true);
    target.search = qs.stringify(Object.assign(target.query, query || {}));
    target = url.format(target);

    const headers = new Headers();
    headers.set('Content-Type', 'application/json');

    const apiKey = this.apiKeyService.getKey();
    if (apiKey) {
      headers.set('Authorization', `Bearer ${apiKey}`);
    }

    debug.info('new request: %s %s %squery: %o, body: %o', method, path, apiKey ? '(authed) ' : '', query, body);

    return new Request(target, {
      method,
      headers,
      body: (method === GET || method === HEAD) ? undefined : JSON.stringify(body),
    });
  }

  rawRequest(method, path, query = null, body = null) {
    const cacheKey = JSON.stringify([method, path, query, body]);
    if ((method === GET || method === HEAD) && this.ongoingRequests[cacheKey]) {
      debug.info('from ongoing request cache: %s %s query: %o, body: %o', method, path, query, body);
      return this.ongoingRequests[cacheKey].then();
    }

    const request = this.buildRequest(method, path, query, body);

    this.eventBus.$emit('start');
    const response = fetch(request)
      .then(res => res.json())
      .then(res => {
        if (res.diff) {
          debug.warn('Diff in format outputs. req: %o, res: %o', request, res);
        }
        return res;
      })
      .then(res => {
        if (!res.errors) {
          this.refreshTokenIfNeeded(res.meta ? res.meta.token_expires_at : null);
        }
        return res;
      })
      .then(res => {
        if (res.errors) {
          if (res.errors.indexOf('Your token has expired') !== -1 || res.errors.indexOf('Invalid auth token') !== -1) {
            if (this.apiKeyService.getKey()) {
              this.vue.$root.$emit('logout:force');
            }
          }
          throw res.errors;
        }
        return res.result;
      })
      .catch(err => {
        this.ongoingRequests[cacheKey] = null;
        this.eventBus.$emit('error');
        throw err;
      })
      .then(res => {
        this.ongoingRequests[cacheKey] = null;
        this.eventBus.$emit('done');
        return res;
      });

    this.ongoingRequests[cacheKey] = response;
    return response.then();
  }

  getEventBus() {
    return this.eventBus;
  }

  refreshTokenIfNeeded(expiresAt) {
    if (expiresAt) {
      const ex = moment(expiresAt);
      const refreshBefore = moment().add(DAYS_BEFORE_TOKEN_REFRESH, 'days');

      if (ex.isValid() && ex.isBefore(refreshBefore)) {
        this.debouncedRefresh();
      }
    }
  }

  autocompleteLocations(search, size, locationTypes, countryCode) {
    return this.rawRequest(GET, 'autocomplete/locations', {
      search,
      size,
      types: locationTypes,
      country_code: countryCode,
    })
      .then(r => r.results);
  }

  autocompleteBusinessCategories(search, size, locale) {
    return this.rawRequest(GET, 'autocomplete/business-categories', {
      search,
      size,
      locale,
    });
  }

  getBusinessCategories() {
    return this.rawRequest(GET, 'resources/business-categories');
  }

  getBusinessCategory(id) {
    return this.rawRequest(GET, 'recommendations/business-category', { id });
  }

  getRecommendation(businessCategoryId, marketingGoalId) {
    return this.rawRequest(GET, 'recommendations/suggest-marketing-goal', {
      business_category_id: businessCategoryId,
      marketing_goal_id: marketingGoalId,
    });
  }

  locationsMetadata(cities, regions, countries, zips) {
    const cityArr = Array.isArray(cities) ? cities : [cities];
    const regionArr = Array.isArray(regions) ? regions : [regions];
    const countryArr = Array.isArray(countries) ? countries : [countries];
    const zipsArr = Array.isArray(zips) ? zips : [zips];
    debug.info('locationsMetadata. cities: %o, regions: %o, countries: %o, zips: %o', cities, regions, countries, zips);

    return this.rawRequest(GET, 'locations-metadata', {
      cities: cityArr,
      regions: regionArr,
      countries: countryArr,
      zips: zipsArr,
    })
      .then(r => r.results);
  }

  potentialReach(target) {
    return this.rawRequest(POST, 'potential-reach', null, target);
  }

  getUserDrafts(params) {
    return this.rawRequest(GET, 'drafts/', params);
  }

  getDraft(id) {
    return this.rawRequest(GET, `drafts/${id}`);
  }

  saveDraft(campaign) {
    const data = _.cloneDeep(campaign);
    delete data.campaign.famCampaign;
    return this.rawRequest(POST, 'drafts', null, data);
  }

  updateDraft(id, campaign) {
    const data = _.cloneDeep(campaign);
    delete data.campaign.famCampaign;
    return this.rawRequest(PATCH, `drafts/${id}`, null, data);
  }

  publishDraft(id) {
    return this.rawRequest(POST, `drafts/${id}/publish`);
  }

  changeTarget(id) {
    return this.rawRequest(POST, `drafts/${id}/change-target`);
  }

  getStatusFlows() {
    return this.rawRequest(GET, 'resources/status-flows');
  }

  getStatuses() {
    return this.rawRequest(GET, 'resources/statuses');
  }

  getInterests() {
    return this.rawRequest(GET, 'resources/interests');
  }

  login(email, password) {
    return this.rawRequest(POST, 'auth/login', null, { email, password });
  }

  getCurrentUser() {
    return this.rawRequest(GET, 'users/current');
  }

  updateUserProfile(params = {}) {
    return this.rawRequest(POST, 'users/update-profile', null, params);
  }

  getGenericConfig() {
    return this.rawRequest(GET, 'config/generic');
  }

  getPublicConfig() {
    return this.rawRequest(GET, 'config/public');
  }

  getLocales() {
    return this.rawRequest(GET, 'config/locales');
  }

  getClusters() {
    return this.rawRequest(GET, 'resources/clusters');
  }

  getPackages() {
    return this.rawRequest(GET, 'resources/packages', { trashed: 1 });
  }

  getAges() {
    return this.rawRequest(GET, 'resources/age-ranges');
  }

  getRadiuses() {
    return this.rawRequest(GET, 'resources/radiuses');
  }

  getAdExamples() {
    return this.rawRequest(GET, 'resources/ad-examples');
  }

  getAdvantages() {
    return this.rawRequest(GET, 'resources/advantages');
  }

  getFAQ() {
    return this.rawRequest(GET, 'resources/faq');
  }

  getObjectives() {
    return this.rawRequest(GET, 'resources/objectives');
  }

  getPostExamples() {
    return this.rawRequest(GET, 'resources/post-examples');
  }

  getCreationModes() {
    return this.rawRequest(GET, 'resources/page-creation-modes');
  }

  getOfflineLocations(limit = 0, offset = 0) {
    return this.rawRequest(GET, 'resources/offline-locations', { limit, offset });
  }

  autocompleteFacebookPages(search) {
    return this.rawRequest(GET, 'autocomplete/facebook-pages', { search });
  }

  claimFacebookPage(id) {
    return this.rawRequest(POST, 'facebook-page/claim', null, { id });
  }

  logout(logoutUrl, body) {
    return this.rawRequest(POST, logoutUrl, null, body);
  }

  sendEmailNotification(id, email) {
    return this.rawRequest(POST, `drafts/${id}/send-email`, null, { email });
  }

  getApiUrl(additional = '') {
    return `${this.apiUrlService.getUrl(ADAPI_VERSION)}${additional}`;
  }

  getLastUpdate() {
    return this.rawRequest(GET, 'resources/last-update');
  }

  getResourceCount() {
    return this.rawRequest(GET, 'resources/resource-count');
  }

  validateUrl(urlToValidate) {
    return this.rawRequest(POST, 'url/validate', null, { urlToValidate });
  }
}

Injector.service('adApiService', ['$context', 'adApiUrlService', 'apiKeyService'], AdApiService);



// WEBPACK FOOTER //
// ./src/services/AdApiService.js